import React, { FC, ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import ProductGenres from '../../shared/product/ProductGenres';
import ProductAge from '../../shared/product/ProductAge';
import { ProductFormat } from '../../shared/product/ProductFormat';
import ProductPublisher from '../../shared/product/ProductPublisher';
import ProductLanguage from '../../shared/product/ProductLanguage';
import LinkWithIconRight from '../../shared/common/LinkWithIconRight';
import ProductCampaignSaleDates from '../../shared/product/ProductCampaignSaleDates';
import CampaignUsageBadge from '../../yourborrowbox/marketing/campaigns/CampaignUsageBadge';
import { THEME_PRIMARY } from '../../styles/basics/colors';
import {
    generateRelatedBundlesForAuthors,
    generateRelatedBundlesForProduct
} from '../../shared/utils/utils-related-bundle';
import { Article } from '../../../models/Article';
import { ProductMetaData } from '../../../models/Product';
import { AddProduct } from '../../shared/common/AddProduct/AddProduct';
import { log } from '../../shared/utils/log';
import MetadataUtils from '../../shared/utils/utils-metadata';
import { useTranslation } from 'react-i18next';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { concreteLoanFormatNameToKey, concreteLoanFormatNameToObject } from '../../shared/loanFormat/mappers';

const StCampaignUsageBadge = styled(CampaignUsageBadge)`
    margin-bottom: 10px;
`;

const SidebarContainer = styled.div`
    width: 215px;
    flex-grow: 0;
`;

const StLinkContainer = styled.div`
    margin-bottom: 15px;
`;

type Props = {
    readonly product: ProductMetaData & Article;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
};

const CampaignProductDetailSidebar: FC<Props> = ({ product, onAddPrice }) => {
    const { t } = useTranslation();
    let relatedBundles: ReactNode = null;

    const relatedBundlesByAuthorInner = generateRelatedBundlesForAuthors(
        product.relatedBundlesByAuthor,
        product.authors
    );
    const relatedBundlesByProductInner = generateRelatedBundlesForProduct(
        product.relatedBundlesByProduct,
        product.titleWithoutPrefix
    );

    if (relatedBundlesByAuthorInner || relatedBundlesByProductInner) {
        relatedBundles = (
            <div className="bundles">
                <h4>{t('menu.shop.loanFormat.curatedCollections')}</h4>
                {relatedBundlesByProductInner}
                {relatedBundlesByAuthorInner}
            </div>
        );
    }

    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const loanFormatSingularName = concreteLoanFormatNameToObject(product.loanFormat)?.singularName ?? null;
    const isPreOrder = MetadataUtils.isProductPreOrderable(product);

    const campaignUsageBadge = (
        <StCampaignUsageBadge
            loanFormat={loanFormatSingularName}
            isSimultaneousAccess={product.simultaneousAccess}
            campaignTerm={product.termLimit}
            usageLimit={product.usageLimit}
            releaseDate={product.releaseDate}
            isPreOrder={isPreOrder}
        />
    );

    const standardProductLink = (
        <StLinkContainer>
            <LinkWithIconRight
                to={`/products/${product.campaignDetails?.standardProductId}`}
                label={t('components.productDetails.availableAsStandardProduct')}
                icon="chevron_right"
                iconColor={THEME_PRIMARY}
            />
        </StLinkContainer>
    );

    const productReleaseDate = product.campaignDetails?.salesEndDate ? (
        <ProductCampaignSaleDates startDate={product.releaseDate!} endDate={product.campaignDetails.salesEndDate} />
    ) : null;

    const handleSelectPrice = useCallback(async () => {
        log.error('onSelectPrice called from campaign product detail sidebar');
    }, []);

    return (
        <SidebarContainer className="ca-side old">
            <div className="ca-side-inner">
                {campaignUsageBadge}

                <AddProduct
                    product={product}
                    onAddPrice={onAddPrice}
                    onSelectPrice={handleSelectPrice}
                    showSelectedPriceOnly={false}
                    allowSelection={true}
                />

                {standardProductLink}

                <ProductFormat
                    product={product}
                    showIcon={true}
                    loanFormatKey={loanFormatKey}
                    shopLanguage={undefined}
                />

                {productReleaseDate}

                <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />

                <ProductLanguage product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />

                <p>{product.isbn13}</p>

                <div className="genres">
                    <h4>{t('menu.shop.loanFormat.genres')}</h4>
                    <ProductGenres
                        product={product}
                        showGenresInSeparateLines="true"
                        showDetails={true}
                        loanFormatKey={loanFormatKey}
                        shopLanguage={undefined}
                    />
                </div>

                <div className="publishers">
                    <h4>{t('menu.shop.loanFormat.publishers')}</h4>
                    <ProductPublisher product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                </div>

                {relatedBundles}
            </div>
        </SidebarContainer>
    );
};

export default CampaignProductDetailSidebar;
